import {
  Directive,
  OnInit,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';

/**
 * Structural directive that conditionally renders content
 * only if the projected content is not empty.
 *
 * It checks whether the template's content has any meaningful text
 * or elements and hides the template if it's considered empty.
 *
 * ```html
 *   <ng-container *appIsEmpty>
 *     <!-- content shown only if not empty -->
 *   </ng-container>
 *  ```
 */
@Directive({
  selector: '[appIsEmpty]'
})
export class IsEmptyDirective implements OnInit {
  /**
   * @param templateRef - A reference to the template to be conditionally rendered.
   * @param viewContainer - The container to insert or clear views based on content.
   */
  constructor(
    private readonly templateRef: TemplateRef<any>,
    private readonly viewContainer: ViewContainerRef
  ) {}

  /**
   * Lifecycle hook that runs once the directive is initialized.
   *
   * It renders the template temporarily to inspect its content. If the content
   * is determined to be empty (no text or child elements), the view is not rendered.
   */
  public ngOnInit(): void {
    const embeddedView = this.viewContainer.createEmbeddedView(this.templateRef);
    embeddedView.detectChanges();

    const isEmpty = embeddedView.rootNodes.every(
      node => !(node.textContent ?? '').trim() && !node.querySelector?.('*')
    );

    this.viewContainer.clear();

    if (!isEmpty) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    }
  }
}
